import { useState } from "react";
import { Link } from "react-router-dom";

import { absoluteTime, relativeTime, shortDateTime } from "../../lib/time";
import type { SalesCall } from "../../lib/sales-types";
import { Avatar } from "../ui/Avatar";
import { Button } from "../ui/Button";
import { cn } from "../ui/cn";
import { AttentionBadge } from "./AttentionBadge";
import { SlaIndicator } from "../SlaClock";
import { salesSla } from "../../lib/sales-sla";
import { useOpenCall } from "../../lib/call-overlay";
import { formatPhone } from "../crm/PhoneField";

/**
 * The number a rep would actually dial for this call, and where it came from.
 *
 * Three numbers can apply and they are not interchangeable, so this picks in
 * the order somebody would ask for them rather than merging them:
 *
 *   1. What was typed when the call was raised. Whoever raised it had a reason
 *      to write down that number instead of the one on file.
 *   2. The linked contact's own number -- the person, if there is one.
 *   3. The account's switchboard. Reception, which always answers and never
 *      gets you the buyer directly.
 *
 * `source` is returned alongside because the difference matters at the moment
 * of dialling: a rep about to ring "Acme" should know whether they are getting
 * Sarah's mobile or a receptionist. It rides in the tooltip rather than on the
 * row, since it is the second question and the row is already dense.
 */
function dialable(
  call: SalesCall,
): { number: string; source: string } | null {
  if (call.phone) return { number: call.phone, source: "Number on this call" };
  if (call.contactPhone) {
    return {
      number: call.contactPhone,
      source: call.contactName ? `${call.contactName}'s number` : "Contact",
    };
  }
  if (call.companyPhone) {
    return { number: call.companyPhone, source: "Company switchboard" };
  }
  return null;
}

/**
 * The number as a one-tap link.
 *
 * Renders nothing at all when there is none, rather than a dash. A dash down a
 * column says "this field is empty and that is notable"; here the absence is
 * ordinary -- plenty of leads arrive without a number -- and a row of dashes
 * would draw the eye to exactly the calls nobody can make.
 *
 * Grouped for reading and dialled as stored: `formatPhone` turns the E.164 on
 * file into "0161 496 0000", while the `href` keeps the raw form, which is
 * what a softphone wants.
 */
function CallPhone({ call }: { call: SalesCall }) {
  const found = dialable(call);
  if (!found) return null;

  return (
    <a
      href={`tel:${found.number}`}
      title={found.source}
      onClick={(event) => event.stopPropagation()}
      className="block truncate text-[11px] tabular-nums text-brand-600 hover:underline dark:text-brand-400"
    >
      {formatPhone(found.number)}
    </a>
  );
}

/**
 * A closed call's note: one line, or all of it, on a click.
 *
 * The note is the reason the closed list exists. A score says "Success" and
 * nothing about why; the note is where somebody wrote that the buyer is back
 * from leave on the 3rd, or that they have gone with a competitor and there is
 * no point ringing again. Truncated to six words it was, as Jade put it, only
 * ever "the first few words".
 *
 * ## Expanded in place, not on the call page
 *
 * The obvious fix is "click through to read it", and the list already links
 * there. But this list is *scanned* -- somebody catching up on a week reads
 * eight of these in a row -- and a navigation per note means eight round trips
 * and eight losses of your place in the list. The whole text is already in the
 * response, so opening it costs nothing.
 *
 * Clicking again collapses it, so a long note cannot leave the row permanently
 * four inches tall once you have finished with it.
 *
 * ## Its own state, per row
 *
 * Deliberately not lifted or persisted. Which note somebody has open is a
 * thing about the next ten seconds, not a preference; carried in the URL or in
 * storage it would come back expanded a week later for no reason anybody could
 * reconstruct.
 */
function ClosingNote({ notes }: { notes: string | null }) {
  const [open, setOpen] = useState(false);

  if (!notes) {
    return (
      <Cell from="lg">
        <span className="text-faint">No note</span>
      </Cell>
    );
  }

  return (
    /*
     * A button rather than a div with a click handler, so it is reachable by
     * keyboard and announced as something that does something. `text-left`
     * because a button centres its text and this is a paragraph.
     */
    <button
      type="button"
      onClick={() => setOpen((was) => !was)}
      aria-expanded={open}
      title={open ? "Hide the full note" : "Show the full note"}
      className={cn(
        "hidden min-w-0 rounded text-left text-[11px] text-muted lg:block",
        "hover:text-content focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
        /* `whitespace-pre-line` keeps the paragraph breaks somebody typed --
           a note written as three bullets should not come back as one run-on
           sentence the moment it is opened. */
        open ? "whitespace-pre-line" : "truncate",
      )}
    >
      {notes}
    </button>
  );
}

/**
 * Who ended the conversation, as a column on the closed list.
 *
 * A face and a first name, the same cluster an open row uses for its assignee.
 * Two reasons it is not simply the full name as text: a column of avatars is
 * scannable in a way a column of names is not -- "which of these did Sam
 * close" is answered by looking, not reading -- and the two lists have no
 * business drawing the same idea two different ways.
 *
 * Not a button, unlike the assignee cluster it resembles. There is nothing to
 * do to a closed call's closer; the resemblance is the point and the
 * interactivity is not.
 *
 * A dash where nobody is recorded, which happens for two different reasons and
 * deliberately looks the same for both: a call closed before this was tracked,
 * and a call closed by somebody whose account has since been deleted -- the
 * `ON DELETE SET NULL` on `closed_by_user_id`. Neither has an answer to give,
 * and inventing a distinction between two kinds of nothing would be a column
 * of footnotes.
 */
function ClosedBy({ call }: { call: SalesCall }) {
  if (call.closedByUserId === null) {
    return (
      <Cell from="lg">
        <span className="text-faint">—</span>
      </Cell>
    );
  }

  return (
    <div
      className="hidden min-w-0 items-center gap-1.5 text-[11px] text-muted lg:flex"
      title={`Closed by ${call.closedByName ?? "a deleted account"}`}
    >
      <Avatar
        agent={{ id: call.closedByUserId, name: call.closedByName }}
        size="xs"
      />
      {/* The first name only, because the track is seven characters wide and
          "Ra…" is not a person. The tooltip carries the whole of it. */}
      <span className="truncate">
        {call.closedByName?.split(" ")[0] ?? "Unknown"}
      </span>
    </div>
  );
}

/**
 * One line of the worklist, as real columns.
 *
 * This was a two-part row: everything about the call wrapped into a flexible
 * block on the left, and the assignee pinned right. On a wide screen that left
 * a lake of empty space in the middle with the content jammed into both
 * corners -- "all squished into both corners", as Jade put it -- and the two
 * dates were crammed into a right-aligned stack too small to read.
 *
 * It is now a fixed grid, so every row's group sits under every other row's
 * group and the list can be read *down* a column as well as across. That is
 * the whole reason a worklist is a table: comparing thirty rows on one field
 * is the job, and a wrapping flex layout makes it impossible because each
 * value lands at a different x position depending on the length of the text
 * beside it.
 *
 * ## Two orders, because the two lists answer different questions
 *
 * Open: company, how late it is, group, account manager, attempts, raised, who
 * has it. Read while deciding whether to ring *this* one.
 *
 * Closed: company, group, account manager, who closed it, when it closed, the
 * score, and the start of the closing note. Read while reviewing what
 * happened, where the outcome matters, "who is holding it" no longer means
 * anything, and "who had this conversation" is the first thing asked.
 *
 * A row is a link, not a click handler, so it opens in a new tab like every
 * other list in Pulse. Claim is a real button, which is why it lives in its
 * own grid cell rather than inside the anchor -- a button inside an anchor is
 * invalid markup and behaves differently in every browser.
 */
export function CallRow({
  call,
  onClaim,
  onRelease,
  onPin,
  claiming,
  currentUserId,
  dense,
  reasonAlreadySaid,
}: {
  call: SalesCall;
  onClaim?: (id: number) => void;
  /** Absent where letting go makes no sense, e.g. the closed list. */
  onRelease?: (id: number) => void;
  /**
   * Pin or unpin, from the list.
   *
   * Absent for everybody but an admin -- pinning changes the order the whole
   * desk works in, so a rep who could pin their own calls would have a
   * queue-jumping button. Same rule the route enforces.
   */
  onPin?: (id: number, pinned: boolean) => void;
  claiming?: boolean;
  currentUserId: number;
  /** Tighter, for the panels on the landing page. */
  dense?: boolean;
  /**
   * The list this row is in is already headed by the reason it is flagged, so
   * the row should not repeat it. See `AttentionBadge`.
   */
  reasonAlreadySaid?: boolean;
}) {
  /* Over this page rather than instead of it, so closing the call comes back
     to the same row -- see `call-overlay.ts`. */
  const openCallById = useOpenCall();
  const mine = call.assigneeUserId === currentUserId;
  const closed = call.status === "closed";

  /* The pill and the badge, drawn beside the name on a wide row and under it
     on a narrow one -- see the subject line. */
  const hasFlags =
    call.pinnedAt !== null ||
    call.attention.waiting ||
    (call.attention.overdue && !reasonAlreadySaid);
  const flags = (
    <>
      {call.pinnedAt ? (
        <span
          title="A manager pinned this to the front of the list"
          className="shrink-0 rounded-full bg-brand-600 px-1.5 py-0.5 text-[10px] font-medium text-white"
        >
          Pinned
        </span>
      ) : null}

      <AttentionBadge
        attention={call.attention}
        size="xs"
        reasonAlreadySaid={reasonAlreadySaid}
      />
    </>
  );

  /**
   * Open the call, unless the click was meant for something inside the row.
   *
   * One rule here rather than `stopPropagation` on each control, which is what
   * the ticket list does -- it has a single checkbox to exempt and this row has
   * five things: the company link, the `tel:` number, Claim, Release and the
   * pin. Five copies of the same guard is five chances to forget the sixth, and
   * the one that gets forgotten is a rep ringing a number and being taken to a
   * dialog instead.
   *
   * `closest` rather than checking the target itself, because the thing
   * actually clicked is usually a span inside the button.
   */
  const openCall = (event: React.MouseEvent): void => {
    if ((event.target as HTMLElement).closest("a, button, input, select")) return;
    openCallById(call.id);
  };

  return (
    <div
      role="row"
      tabIndex={0}
      onClick={openCall}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        /* Only the row itself. Enter on the Claim button inside it is that
           button's, and space would scroll the list. */
        if (event.target !== event.currentTarget) return;
        event.preventDefault();
        openCallById(call.id);
      }}
      className={cn(
        "cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500/40",
        /*
         * Three tiers, because the sidebar takes 420px before this list gets
         * any.
         *
         * All six columns need roughly 860px of *pane* to be readable, which
         * is a viewport around 1280 -- at 1024 the group and account manager
         * columns come out as "Hi…" and "Ra…", which is worse than not showing
         * them. So the full set waits for `xl`, `lg` gets the four that fit,
         * and a phone gets the company and the action.
         *
         * The company column is the only flexible one; the rest are fixed so
         * they line up down the list. Fixed rather than `auto` deliberately --
         * `auto` sizes to the widest cell, which means one long group name
         * shifts every column on every row.
         */
        /* Items sit at the top on a closed row rather than centred: an
           expanded note is several lines tall, and centring would float the
           company name into the middle of the row beside it. */
        "group grid gap-2 border-b border-line sm:gap-3",
        closed ? "items-start" : "items-center",
        /* The action track is fixed rather than `auto` on an open row, because
           it carries the pin: a track that sizes to its own content starts at
           a different x on every row, which is precisely what threw the pins
           out of line. A closed row keeps `auto` at this width -- it has no
           action cell at all on a narrow screen, and a fixed track would be
           7rem of nothing down the right-hand edge.

           5.5rem on a phone, which is the pin and a Claim button with nothing
           to spare. The assignee's first name is not drawn below `sm`, so the
           extra 1.5rem the track carries for it was being taken from the
           company name -- on a 375px screen, the difference between "Hen…" and
           a name somebody can read. */
        closed
          ? "grid-cols-[minmax(0,1fr)_auto]"
          : "grid-cols-[minmax(0,1fr)_auto_5.5rem] sm:grid-cols-[minmax(0,1fr)_auto_7rem]",
        /*
         * Two sets of tracks, because the two rows carry different things.
         *
         * They shared one, and the closing note inherited the 7rem the open
         * row uses for its assignee -- which is about six words, and is why a
         * note could only ever be read as "Spoke to Sarah, she is…". The note
         * now gets the widest flexible track on the row and the company gives
         * up some of its own: on a closed call the name is a label you have
         * already read, and what happened is the thing you came for.
         *
         * It also drops to `lg` from `xl`. The note is the *point* of the
         * closed list, so hiding it on the width most of the desk works at was
         * the wrong column to sacrifice -- the account manager goes instead,
         * which on a closed call no longer means anything anyway.
         */
        closed
          ? [
              "lg:grid-cols-[minmax(0,1.3fr)_minmax(0,0.8fr)_minmax(0,0.8fr)_7rem_7rem_minmax(0,1.7fr)]",
              "xl:grid-cols-[minmax(0,1.4fr)_minmax(0,0.7fr)_minmax(0,0.7fr)_minmax(0,0.8fr)_7rem_7.5rem_minmax(0,2fr)]",
            ]
          : [
              "lg:grid-cols-[minmax(0,2fr)_6.5rem_minmax(0,1fr)_7rem_7rem]",
              "xl:grid-cols-[minmax(0,2fr)_6.5rem_minmax(0,0.9fr)_minmax(0,0.9fr)_7rem_7.5rem_7rem]",
            ],
        "hover:bg-surface-2",
        /* Room at both ends. These were flush against the pane edges, which
           made a wide screen read as an unbounded table rather than a list. */
        dense ? "px-3 py-2 sm:px-4" : "px-3 py-2.5 sm:px-5",
        /* A left rule rather than a tinted row. Twenty tinted rows read as a
           broken table; twenty red edges read as a list with a problem in it.
           Inset by the padding so it does not eat the new margin. */
        call.attention.overdue && "border-l-2 border-l-red-500",
        call.pinnedAt &&
          !call.attention.overdue &&
          "border-l-2 border-l-brand-500",
      )}
    >
      {/*
        1 ── The company, and anything that changes how urgently to read it.

        The name is the one thing in this row that does *not* open the call.
        The whole bar does that now, so a link covering most of it would only
        be a second way to the same place -- and the question a rep actually
        has when they stop on a name is "who are these people", which is the
        CRM account: who manages it, what they last bought, what else is open.
        Getting there used to mean leaving the worklist and searching for them.

        Plain text when the call has no linked account. A dead link that looks
        live is worse than a name that was never offered as one, and a call
        raised against a company nobody has filed yet is ordinary.
      */}
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-1.5">
          {call.companyId !== null ? (
            <Link
              to={`/crm/companies/${call.companyId}`}
              title={`Open ${call.companyName} in the CRM`}
              className="truncate text-sm font-medium text-content hover:text-brand-600 hover:underline dark:hover:text-brand-400"
            >
              {call.companyName}
            </Link>
          ) : (
            <span className="truncate text-sm font-medium text-content">
              {call.companyName}
            </span>
          )}

          <span className="hidden shrink-0 items-center gap-1.5 lg:flex">
            {flags}
          </span>
        </div>

        {/* The subject stays under the name rather than taking a column of its
            own: it is free text of any length, so a column of it would be
            mostly ellipsis.

            Below `lg` the flags come down to this line, ahead of the subject.
            Beside the name they are shrink-proof and the name is not, so on a
            phone "Going cold" left room for three letters of the company --
            and the subject is the one thing on the row that can give way to
            them without anybody missing it. */}
        {call.subject || hasFlags ? (
          <div className="flex min-w-0 items-center gap-1.5">
            <span className="flex shrink-0 items-center gap-1.5 empty:hidden lg:hidden">
              {flags}
            </span>
            {call.subject ? (
              <span className="min-w-0 truncate text-[11px] text-muted">{call.subject}</span>
            ) : null}
          </div>
        ) : null}

      {/* Who closed it and when, on the narrower screens that have no room
          for those columns. A phone is where a rep picking up somebody else's
          conversation reads this list, and "who do I ask" was the one thing a
          closed row could not tell them there. */}
      {closed ? (
        <div className="truncate text-[11px] text-muted lg:hidden">
          Closed by {call.closedByName ?? "somebody"}
          {call.closedAt ? ` · ${shortDateTime(call.closedAt)}` : ""}
        </div>
      ) : null}

      {/*
        The number to ring, on the row.

        It was nowhere on this screen: a rep looking at the worklist had to
        open the CRM account, copy the number, and come back -- for every call,
        including the ones they decide not to make. The record already knew it.

        Shown at every width, unlike the columns beside it. On a phone this is
        the single most useful thing in the row, and it is the one field the
        list exists to act on.
      */}
      <CallPhone call={call} />
      </div>

      {/*
        2 ── How late it is, in a column of its own.

        This used to sit inline after the company name, and it is the single
        change that fixes the row. Three shrink-proof things shared that line
        -- the name, the overdue pill and this -- and the name was the only one
        of them that could give way, so it gave way to nothing: "Pennine Sup…",
        "Northgate Ele…", "Coastal Mari…" down the whole list, with the
        countdown wrapping onto a second line beside it and breaking the row
        rhythm as it went.

        A column also makes it *comparable*, which is the point of a worklist.
        Inline after a name it landed at a different x on every row and could
        only be read one call at a time; in a track it can be scanned down.

        Fixed at 6.5rem rather than `auto`, for the reason the other fixed
        tracks give: `auto` sizes to the widest cell, so one badly overdue call
        would shift every column on every row. 6.5rem is the realistic worst
        case -- "24h 03m over", the longest form the countdown takes before it
        switches to days. Present at every width, closed rows excepted -- a
        closed call has no clock, which is why the closed templates carry no
        track for one.

        Left-aligned, unlike the numeric tracks further along the row. Those
        are read as magnitudes and want their digits stacked; this one is read
        as a *dial*, and the dial is the primary signal -- colour and fill,
        with the countdown as the second opinion. Right-aligning put the right
        edge of the text at a fixed x and let the dials wander by a
        centimetre from row to row, which is the same complaint the pin has
        its own track for.
      */}
      {!closed ? (
        <div className="flex min-w-0 items-center">
          {/* The same clock the ticket list uses, fed the call's own rules --
              see `salesSla`. Time left while there is some, time over once
              there is not. */}
          <SlaIndicator sla={salesSla(call)} />
        </div>
      ) : null}

      {/* 3 ── Group. Kept at `lg`: it is the field the list is most often
          scanned by, and the one Jade asked to see first after the name. */}
      <Cell from="lg">
        {call.groupName ?? <span className="text-faint">Ungrouped</span>}
      </Cell>

      {/*
        4 ── The CRM's account manager, not the rep holding the call. Worth a
        column of its own: ringing somebody else's account without knowing it
        is somebody else's is the mistake this prevents, and it is invisible
        unless the row says so.
      */}
      <Cell from="xl" title="Account manager in the CRM">
        {call.accountManagerName ? (
          call.accountManagerName
        ) : call.companyId ? (
          <span className="text-faint">Unmanaged</span>
        ) : (
          <span className="text-faint">—</span>
        )}
      </Cell>

      {closed ? (
        <>
          {/*
            5 ── Who closed it.

            The one thing a closed row could not answer, and the question most
            often asked of it: a manager reviewing the week, or a rep picking up
            a conversation somebody else ended, wants to know who to go and
            speak to before they read the note. It was on the call page all
            along, one navigation away per row -- and this list is read eight
            rows at a time, which is eight navigations to answer one question.

            An avatar and a first name, matching the assignee cluster on an
            open row: a column of faces is scannable in a way a column of full
            names is not, and the two lists should not invent different ways to
            draw the same thing.

            It appears at `lg`, one width earlier than the account manager
            sitting to its left, so on the size most of the desk works at this
            column is shown and that one is not. On a closed call "who is
            managing the account" is background; "who had this conversation" is
            the record.
          */}
          <ClosedBy call={call} />

          {/* 6 ── When it closed. */}
          <Cell from="lg" align="right" title={call.closedAt ? absoluteTime(call.closedAt) : ""}>
            <span className="tabular-nums">
              {call.closedAt ? shortDateTime(call.closedAt) : "—"}
            </span>
          </Cell>

          {/* 7 ── The score. */}
          <Cell from="lg" align="right">
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-[11px] font-medium",
                toneClass(call.scoreTone),
              )}
            >
              {call.scoreLabel ?? "Closed"}
            </span>
          </Cell>

          {/* 8 ── The closing note, in full if you ask for it. */}
          <ClosingNote notes={call.closingNotes} />
        </>
      ) : (
        <>
          {/*
            5 ── Attempts and staleness as one phrase, not two columns. "3
            tries, last 6 days ago" is how somebody says it out loud, and
            either half alone is misleading -- three attempts today and three
            last month are completely different situations.
          */}
          <Cell from="lg" align="right">
            {call.attemptCount === 0 ? (
              <span className="text-faint">Not tried</span>
            ) : (
              <span
                className={cn(
                  call.attention.overdue && "text-red-600 dark:text-red-400",
                )}
                title={
                  call.lastAttemptAt
                    ? `Last attempt ${absoluteTime(call.lastAttemptAt)}`
                    : undefined
                }
              >
                <span className="tabular-nums">{call.attemptCount}</span>
                {", "}
                {relativeTime(call.lastAttemptAt ?? call.createdAt)}
              </span>
            )}
          </Cell>

          {/* 6 ── Raised on, with the time.
              
              An actual timestamp rather than "6d ago": this column is compared
              row against row, and relative phrases of differing lengths are
              far harder to line up than two dates. The time is there because
              two calls raised the same morning are not interchangeable -- one
              came in before the desk opened and one after lunch. */}
          <Cell from="xl" align="right" title={`Raised ${absoluteTime(call.createdAt)}`}>
            <span className="tabular-nums">{shortDateTime(call.createdAt)}</span>
          </Cell>

          {/*
            7 ── The pin, and who has it.

            Two tracks rather than one right-hugging row. The pin used to sit
            in front of whatever came next in a `justify-end` flex, so it was
            pushed along by the width of that thing: a row offering "Claim" put
            its pin in one place, a row showing an avatar and a name put it
            somewhere else, and down a list the pins wandered by half a
            centimetre from line to line. A track of its own fixes the pin's x
            for every row, and the assignee still hugs the right-hand edge.

            The track is 1rem because that is exactly the button -- a 12px
            glyph in 2px of padding.
          */}
          <div
            className={cn(
              "grid items-center gap-2",
              onPin ? "grid-cols-[1rem_minmax(0,1fr)]" : "grid-cols-1",
            )}
          >
            {/*
              Pin, on the row.

              It was only on the call page, which meant reordering the queue
              cost an open and a close per call -- and the queue is exactly the
              thing you reorder while looking at all of it.

              Always drawn, never hover-only. It first appeared on hover so a
              list at rest would not be a row of icons, which was the wrong
              trade: a control you cannot see is one nobody uses, and on a
              touch screen there is no hover to reveal it at all. Grey when the
              call is unpinned, brand pink when it is, so the state is legible
              down the column without pointing at anything.
            */}
            {onPin ? (
              <button
                type="button"
                onClick={() => onPin(call.id, !call.pinnedAt)}
                disabled={claiming}
                title={
                  call.pinnedAt
                    ? "Unpin — put it back in the usual order"
                    : "Pin it to the front of the list for everybody"
                }
                aria-label={call.pinnedAt ? "Unpin this call" : "Pin this call"}
                aria-pressed={Boolean(call.pinnedAt)}
                className={cn(
                  "shrink-0 rounded p-0.5 transition-colors hover:bg-surface-2",
                  call.pinnedAt
                    ? "text-brand-600 dark:text-brand-400"
                    : "text-faint hover:text-brand-500",
                )}
              >
                <PinIcon filled={Boolean(call.pinnedAt)} />
              </button>
            ) : null}

            <div className="flex min-w-0 items-center justify-end">
              {call.assigneeUserId === null ? (
                onClaim ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={claiming}
                    onClick={() => onClaim(call.id)}
                  >
                    {claiming ? "Claiming…" : "Claim"}
                  </Button>
                ) : (
                  <span className="text-[11px] text-faint">In the pool</span>
                )
              ) : mine && onRelease ? (
                /*
                  Your own name, as the button that gives the call back.

                  The whole cluster -- avatar, name and cross -- is one target
                  rather than a cross beside a label, because "click your name
                  to drop it" is how somebody describes this out loud, and a
                  20px cross is a poor thing to aim at on a list you are
                  scanning quickly. The cross stays as the affordance: it
                  appears on hover so a row at rest is not a wall of delete
                  icons.
                */
                <button
                  type="button"
                  onClick={() => onRelease(call.id)}
                  disabled={claiming}
                  title="Click to put this call back in the pool"
                  aria-label="Put this call back in the pool"
                  className={cn(
                    "flex min-w-0 items-center gap-1.5 rounded-full py-0.5 pl-0.5 pr-1",
                    "transition-colors hover:bg-surface-2",
                    claiming && "opacity-50",
                  )}
                >
                  <Avatar
                    agent={{ id: call.assigneeUserId, name: call.assigneeName }}
                    size="xs"
                  />
                  <span className="hidden text-[11px] text-faint sm:inline">
                    You
                  </span>
                  <span
                    aria-hidden
                    className="flex size-4 items-center justify-center text-faint opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
                  >
                    <svg
                      width="9"
                      height="9"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                    >
                      <path d="M6 6l12 12M18 6L6 18" />
                    </svg>
                  </span>
                </button>
              ) : (
                <span
                  className="flex min-w-0 items-center gap-1.5"
                  title={call.assigneeName ?? ""}
                >
                  <Avatar
                    agent={{ id: call.assigneeUserId, name: call.assigneeName }}
                    size="xs"
                  />
                  {/* `block`, not the inline it was: `truncate` does nothing to
                      an inline box, so a long first name used to widen the
                      cluster and shove the pin left again. */}
                  <span className="hidden truncate text-[11px] text-faint sm:block">
                    {call.assigneeName?.split(" ")[0] ?? ""}
                  </span>
                </span>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * A drawing pin, filled when the call is pinned.
 *
 * Filled versus outlined rather than two different glyphs: the state has to be
 * readable at 12px in a row somebody is scanning, and a shape change at that
 * size registers where a colour change alone does not.
 */
function PinIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 17v5" />
      <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" />
    </svg>
  );
}

/**
 * One column of a row, appearing at the width it can actually be read at.
 *
 * `from` has to match the grid template above: a cell that appears at `lg`
 * when the template only defines four columns there would be placed in an
 * implicit fifth and break the alignment for the whole row. The two are
 * changed together or not at all.
 */
function Cell({
  children,
  from,
  align,
  title,
}: {
  children: React.ReactNode;
  from: "lg" | "xl";
  align?: "right";
  title?: string;
}) {
  return (
    <div
      title={title}
      className={cn(
        "hidden min-w-0 truncate text-[11px] text-muted",
        from === "lg" ? "lg:block" : "xl:block",
        align === "right" && "text-right",
      )}
    >
      {children}
    </div>
  );
}

/**
 * Score colours from the tone a manager set, not from the label.
 *
 * The list of scores is theirs to change, so nothing here may key on the word
 * "Success" -- a desk that renames it to "Won" would silently lose its colour.
 */
export function toneClass(tone: string | null): string {
  if (tone === "positive") {
    return "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300";
  }
  if (tone === "negative") {
    return "bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300";
  }
  return "bg-surface-2 text-muted-on-tint";
}
